import { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, Filter, X, ShieldCheck, Loader2, ChevronDown, ChevronUp, Download, Trash2,
  Pencil, Save, ShieldAlert, ShieldX, ShieldOff,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface Warranty {
  id: string;
  productName?: string;
  product?: { id: string; name: string } | null;
  serialNumber?: string;
  contactName?: string;
  contactPhone?: string;
  startDate: string;
  endDate: string;
  terms?: string;
  status: string;
  account?: { id: string; name: string } | null;
  initialCondition?: string;
  returnCondition?: string;
  conditionPhotos?: string[];
  machineStatus?: string;
  serviceBranch?: string;
  dispatchedAt?: string;
  returnedAt?: string;
  returnNotes?: string;
  source?: 'manual' | 'dispatch_load' | string;
  sourceRef?: string | null;
  registeredAt?: string | null;
}

interface Product {
  id: string;
  name: string;
}

interface Account {
  id: string;
  name: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
}

const STATUSES = ['active', 'expiring_soon', 'expired', 'claimed'];

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-50 text-green-700',
  expiring_soon: 'bg-yellow-50 text-yellow-700',
  expired: 'bg-red-50 text-red-700',
  claimed: 'bg-blue-50 text-blue-700',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
  claimed: 'Claimed',
};

const MACHINE_STATUSES = ['with_customer', 'in_service', 'returned', 'scrapped', 'in_transit'];

const MACHINE_STATUS_COLORS: Record<string, string> = {
  with_customer: 'bg-green-50 text-green-700',
  in_service: 'bg-yellow-50 text-yellow-700',
  returned: 'bg-blue-50 text-blue-700',
  scrapped: 'bg-red-50 text-red-700',
  in_transit: 'bg-orange-50 text-orange-700',
};

const MACHINE_STATUS_LABELS: Record<string, string> = {
  with_customer: 'With Customer',
  in_service: 'In Service',
  returned: 'Returned',
  scrapped: 'Scrapped',
  in_transit: 'In Transit',
};

const IST_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function istDay(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 330 * 60 * 1000);
  return `${String(d.getUTCDate()).padStart(2, '0')} ${IST_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
function sourceLabel(w: Warranty): string {
  return w.source === 'dispatch_load' ? `${w.sourceRef ?? 'Dispatch'} load scan` : 'Manual';
}

export function WarrantiesPage() {
  const [warranties, setWarranties] = useState<Warranty[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  const [sortField, setSortField] = useState<string>('endDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Create Warranty Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wForm, setWForm] = useState({
    productId: '', serialNumber: '', contactName: '', contactPhone: '',
    accountId: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    terms: '',
  });

  // Edit Warranty Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '', productId: '', serialNumber: '', contactName: '', contactPhone: '',
    accountId: '',
    startDate: '', endDate: '', terms: '', status: 'active',
    machineStatus: 'with_customer', serviceBranch: '',
    initialCondition: '', returnCondition: '', returnNotes: '',
    dispatchedAt: '', returnedAt: '',
    conditionPhotos: [] as string[],
  });

  const fetchWarranties = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = { page: String(page), limit: String(limit) };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortDir = sortDir;
      const res = await api.get('/warranties', { params });
      setWarranties(res.data.data?.warranties ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load warranties');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, sortField, sortDir, page]);

  useEffect(() => {
    fetchWarranties();
    api.get('/products').then(res => setProducts(res.data.data?.products ?? res.data.data ?? [])).catch(() => {});
    api.get('/accounts').then(res => setAccounts(res.data.data?.accounts ?? res.data.data ?? [])).catch(() => {});
    api.get('/contacts').then(res => setContacts(res.data.data?.contacts ?? res.data.data ?? [])).catch(() => {});
  }, [fetchWarranties]);

  const handleCreateWarranty = async () => {
    try {
      setSaving(true);
      await api.post('/warranties', {
        productId: wForm.productId,
        serialNumber: wForm.serialNumber || undefined,
        contactName: wForm.contactName || undefined,
        contactPhone: wForm.contactPhone || undefined,
        accountId: wForm.accountId || undefined,
        startDate: wForm.startDate,
        endDate: wForm.endDate,
        terms: wForm.terms || undefined,
      });
      setShowModal(false);
      setWForm({ productId: '', serialNumber: '', contactName: '', contactPhone: '', accountId: '', startDate: new Date().toISOString().split('T')[0], endDate: '', terms: '' });
      fetchWarranties();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create warranty');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this warranty record?')) return;
    try {
      await api.delete(`/warranties/${id}`);
      fetchWarranties();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete warranty');
    }
  };

  const openEditModal = (w: Warranty) => {
    setEditForm({
      id: w.id,
      productId: w.product?.id || '',
      serialNumber: w.serialNumber || '',
      contactName: w.contactName || '',
      contactPhone: w.contactPhone || '',
      accountId: w.account?.id || '',
      startDate: w.startDate ? w.startDate.split('T')[0] : '',
      endDate: w.endDate ? w.endDate.split('T')[0] : '',
      terms: w.terms || '',
      status: w.status || 'active',
      machineStatus: w.machineStatus || 'with_customer',
      serviceBranch: w.serviceBranch || '',
      initialCondition: w.initialCondition || '',
      returnCondition: w.returnCondition || '',
      returnNotes: w.returnNotes || '',
      dispatchedAt: w.dispatchedAt ? w.dispatchedAt.split('T')[0] : '',
      returnedAt: w.returnedAt ? w.returnedAt.split('T')[0] : '',
      conditionPhotos: w.conditionPhotos || [],
    });
    setShowEditModal(true);
  };

  const handleEditWarranty = async () => {
    try {
      setEditSaving(true);
      await api.put(`/warranties/${editForm.id}`, {
        productId: editForm.productId || undefined,
        serialNumber: editForm.serialNumber || undefined,
        contactName: editForm.contactName || undefined,
        contactPhone: editForm.contactPhone || undefined,
        accountId: editForm.accountId || undefined,
        startDate: editForm.startDate,
        endDate: editForm.endDate,
        terms: editForm.terms || undefined,
        status: editForm.status,
        machineStatus: editForm.machineStatus || undefined,
        serviceBranch: editForm.serviceBranch || undefined,
        initialCondition: editForm.initialCondition || undefined,
        returnCondition: editForm.returnCondition || undefined,
        returnNotes: editForm.returnNotes || undefined,
        dispatchedAt: editForm.dispatchedAt || undefined,
        returnedAt: editForm.returnedAt || undefined,
      });
      setShowEditModal(false);
      fetchWarranties();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update warranty');
    } finally {
      setEditSaving(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const [downloadingCert, setDownloadingCert] = useState<string | null>(null);

  const handleDownloadCert = async (warrantyId: string) => {
    try {
      setDownloadingCert(warrantyId);
      const res = await api.get(`/warranties/${warrantyId}/certificate`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err: any) {
      alert('Failed to generate warranty certificate');
    } finally {
      setDownloadingCert(null);
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Warranties</h1>
          <p className="text-sm text-gray-500">Track product warranties and service coverage</p>
          <p className="text-xs text-gray-400">Warranties from dispatch are created at the load scan; manual create is for old sales.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Create Warranty
        </button>
      </div>

      {/* Summary Cards */}
      {!loading && !error && warranties.length > 0 && (() => {
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
        const activeCount = warranties.filter(w => w.status === 'active').length;
        const expiringSoonCount = warranties.filter(w => w.status === 'expiring_soon' || (w.status === 'active' && new Date(w.endDate) <= thirtyDaysFromNow)).length;
        const expiredCount = warranties.filter(w => w.status === 'expired').length;
        return (
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--saffron-50)]"><ShieldCheck size={20} className="text-[var(--ink-0)]" /></div>
                <div><p className="text-xs font-medium text-gray-500">Total Warranties</p><p className="text-lg font-bold text-gray-900">{warranties.length}</p></div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50"><ShieldCheck size={20} className="text-green-600" /></div>
                <div><p className="text-xs font-medium text-gray-500">Active</p><p className="text-lg font-bold text-gray-900">{activeCount}</p></div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-50"><ShieldAlert size={20} className="text-yellow-600" /></div>
                <div><p className="text-xs font-medium text-gray-500">Expiring Soon</p><p className="text-lg font-bold text-gray-900">{expiringSoonCount}</p></div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50"><ShieldX size={20} className="text-red-600" /></div>
                <div><p className="text-xs font-medium text-gray-500">Expired</p><p className="text-lg font-bold text-gray-900">{expiredCount}</p></div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search warranties..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Statuses</option>
            {STATUSES.map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
            ))}
          </select>
          {filterStatus && (
            <button
              onClick={() => { setFilterStatus(''); setPage(1); }}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading warranties...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchWarranties} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : warranties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <ShieldCheck size={40} className="mb-2" />
            <p className="text-sm">No warranties found</p>
            <p className="text-xs">Warranties will appear here once added</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('productName')}>
                    <span className="flex items-center gap-1">Product Name <SortIcon field="productName" /></span>
                  </th>
                  <th className="px-4 py-3">Serial Number</th>
                  <th className="px-4 py-3">Account</th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('startDate')}>
                    <span className="flex items-center gap-1">Start Date <SortIcon field="startDate" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('endDate')}>
                    <span className="flex items-center gap-1">End Date <SortIcon field="endDate" /></span>
                  </th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Registered</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Machine Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {warranties.map(w => (
                  <tr key={w.id} className="hover:bg-gray-50 transition-colors cursor-pointer">
                    <td className="px-4 py-3 font-medium text-gray-900">{w.productName || w.product?.name || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{w.serialNumber || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{w.account?.name || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(w.startDate)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(w.endDate)}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{sourceLabel(w)}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{w.registeredAt ? istDay(w.registeredAt) : 'Not yet'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[w.status] || STATUS_COLORS.active}`}>
                        {STATUS_LABELS[w.status] || w.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {w.machineStatus ? (
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${MACHINE_STATUS_COLORS[w.machineStatus] || 'bg-gray-100 text-gray-600'}`}>
                          {MACHINE_STATUS_LABELS[w.machineStatus] || w.machineStatus}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={(e) => { e.stopPropagation(); openEditModal(w); }} className="rounded p-1 text-gray-400 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)]" title="Edit"><Pencil size={14} /></button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadCert(w.id); }}
                          disabled={downloadingCert === w.id}
                          className="flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                          title="Download Warranty Certificate"
                        >
                          {downloadingCert === w.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                          Certificate
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(w.id); }} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <div className="text-sm text-gray-400">
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
              <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Create Warranty Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Create Warranty</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product *</label>
                <select
                  value={wForm.productId}
                  onChange={(e) => setWForm({ ...wForm, productId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                >
                  <option value="">Select Product</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Account</label>
                <select
                  value={wForm.accountId}
                  onChange={(e) => setWForm({ ...wForm, accountId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                >
                  <option value="">Select Account</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Serial Number</label>
                <input type="text" value={wForm.serialNumber} onChange={(e) => setWForm({ ...wForm, serialNumber: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Contact Name</label>
                  <input type="text" value={wForm.contactName} onChange={(e) => setWForm({ ...wForm, contactName: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Contact Phone</label>
                  <input type="tel" value={wForm.contactPhone} onChange={(e) => setWForm({ ...wForm, contactPhone: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Start Date *</label>
                  <input type="date" value={wForm.startDate} onChange={(e) => setWForm({ ...wForm, startDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">End Date *</label>
                  <input type="date" value={wForm.endDate} onChange={(e) => setWForm({ ...wForm, endDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Terms</label>
                <textarea value={wForm.terms} onChange={(e) => setWForm({ ...wForm, terms: e.target.value })} rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setShowModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreateWarranty} disabled={saving || !wForm.productId || !wForm.startDate || !wForm.endDate}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Warranty
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Warranty Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowEditModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Edit Warranty</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product</label>
                <select value={editForm.productId} onChange={e => setEditForm({ ...editForm, productId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                  <option value="">Select Product</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Serial Number</label>
                <input type="text" value={editForm.serialNumber} onChange={e => setEditForm({ ...editForm, serialNumber: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Account</label>
                <select value={editForm.accountId} onChange={e => setEditForm({ ...editForm, accountId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                  <option value="">Select Account</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Contact Name</label>
                  <input type="text" value={editForm.contactName} onChange={e => setEditForm({ ...editForm, contactName: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Contact Phone</label>
                  <input type="tel" value={editForm.contactPhone} onChange={e => setEditForm({ ...editForm, contactPhone: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Start Date *</label>
                  <input type="date" value={editForm.startDate} onChange={e => setEditForm({ ...editForm, startDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">End Date *</label>
                  <input type="date" value={editForm.endDate} onChange={e => setEditForm({ ...editForm, endDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                  {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Machine Status</label>
                <select value={editForm.machineStatus} onChange={e => setEditForm({ ...editForm, machineStatus: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                  {MACHINE_STATUSES.map(s => <option key={s} value={s}>{MACHINE_STATUS_LABELS[s] || s}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Service Branch</label>
                <input type="text" value={editForm.serviceBranch} onChange={e => setEditForm({ ...editForm, serviceBranch: e.target.value })}
                  placeholder="Branch or service location"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Dispatched Date</label>
                  <input type="date" value={editForm.dispatchedAt} onChange={e => setEditForm({ ...editForm, dispatchedAt: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Returned Date</label>
                  <input type="date" value={editForm.returnedAt} onChange={e => setEditForm({ ...editForm, returnedAt: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Initial Condition</label>
                <textarea value={editForm.initialCondition} onChange={e => setEditForm({ ...editForm, initialCondition: e.target.value })} rows={2}
                  placeholder="Condition when dispatched to customer"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Return Condition</label>
                <textarea value={editForm.returnCondition} onChange={e => setEditForm({ ...editForm, returnCondition: e.target.value })} rows={2}
                  placeholder="Condition when returned from customer"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Return Notes</label>
                <textarea value={editForm.returnNotes} onChange={e => setEditForm({ ...editForm, returnNotes: e.target.value })} rows={2}
                  placeholder="Additional notes about the return"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              {/* Condition Photos */}
              {editForm.conditionPhotos.length > 0 && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Condition Photos</label>
                  <div className="grid grid-cols-3 gap-2">
                    {editForm.conditionPhotos.map((url, idx) => (
                      <a
                        key={idx}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block overflow-hidden rounded-lg border border-gray-200 hover:border-[var(--ink-3)] transition-colors"
                      >
                        <img
                          src={url}
                          alt={`Condition ${idx + 1}`}
                          className="h-20 w-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            (e.target as HTMLImageElement).parentElement!.innerHTML = `<div class="flex h-20 items-center justify-center bg-gray-50 text-gray-400 text-xs">Photo ${idx + 1}</div>`;
                          }}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Terms</label>
                <textarea value={editForm.terms} onChange={e => setEditForm({ ...editForm, terms: e.target.value })} rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setShowEditModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleEditWarranty} disabled={editSaving || !editForm.startDate || !editForm.endDate}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {editSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
